/**
 * Trip write path.
 *
 * Two responsibilities beyond the obvious: keep the learned master data in
 * step with what the driver typed, and enforce the record lock.
 *
 * The lock implements the safe harbour in BMF 03.03.2022 Rn. 29 — trips
 * captured on completion with the business details added within seven calendar
 * days are accepted as zeitnah. After that window a trip is frozen. It can
 * still be corrected, because a logbook you cannot fix is a logbook that
 * carries a known error forever, but only as an amendment with a stated
 * reason, which the audit chain records and every export shows.
 */

import type { AuditContext } from '@/db/audit';
import { nowIso, todayIso, addDays, minutesBetween } from '@/domain/dates';
import type {
  Address,
  IsoDate,
  Order,
  Passenger,
  PaymentMethod,
  Shift,
  Trip,
  TripType,
  Uuid,
} from '@/domain/types';
import {
  addressRepo,
  orderRepo,
  passengerRepo,
  shiftForDate,
  shiftRepo,
  tripRepo,
  tripsForShift,
} from '@/db/repositories';
import { normalise, parseName } from '@/suggest';

// ---------------------------------------------------------------------------
// Master data upsert
// ---------------------------------------------------------------------------

/**
 * Find a passenger by name or create one.
 *
 * Matching is on normalised first plus last name, so `Kluse, Michael` typed
 * two different ways does not produce two customers. The `useCount` bump is
 * what makes the suggestion engine put the right person first next time.
 */
export async function resolveOrCreatePassenger(
  ctx: AuditContext,
  existing: readonly Passenger[],
  input: { id?: Uuid; rawName?: string; firstName?: string; lastName?: string; company?: string },
): Promise<Passenger> {
  if (input.id) {
    const found = existing.find((passenger) => passenger.id === input.id);
    if (found) {
      return passengerRepo.update(ctx, found.id, {
        useCount: found.useCount + 1,
        lastUsedAt: nowIso(),
      });
    }
  }

  const parsed = input.rawName
    ? parseName(input.rawName)
    : { firstName: input.firstName ?? '', lastName: input.lastName ?? '' };

  const key = `${normalise(parsed.firstName)}|${normalise(parsed.lastName)}`;
  const match = existing.find(
    (passenger) => `${normalise(passenger.firstName)}|${normalise(passenger.lastName)}` === key,
  );

  if (match) {
    return passengerRepo.update(ctx, match.id, {
      useCount: match.useCount + 1,
      lastUsedAt: nowIso(),
    });
  }

  const passenger: Passenger = {
    id: crypto.randomUUID(),
    firstName: parsed.firstName,
    lastName: parsed.lastName,
    company: input.company,
    isBusiness: Boolean(input.company),
    createdAt: nowIso(),
    useCount: 1,
    lastUsedAt: nowIso(),
  };
  return passengerRepo.create(ctx, passenger);
}

/**
 * Find an address by label or create one.
 *
 * Coordinates from the geocoder are merged into an existing record rather than
 * spawning a duplicate, so an address first typed by hand gains its
 * coordinates the first time it is picked from the suggestion list.
 */
export async function resolveOrCreateAddress(
  ctx: AuditContext,
  existing: readonly Address[],
  input: {
    id?: Uuid;
    label: string;
    street?: string;
    houseNumber?: string;
    postalCode?: string;
    city?: string;
    country?: string;
    lat?: number;
    lon?: number;
    geocoded?: boolean;
  },
): Promise<Address> {
  const bump = { useCount: 0, lastUsedAt: nowIso() };

  if (input.id) {
    const found = existing.find((address) => address.id === input.id);
    if (found) {
      const patch: Partial<Address> = { ...bump, useCount: found.useCount + 1 };
      if (input.lat !== undefined && found.lat === undefined) {
        patch.lat = input.lat;
        patch.lon = input.lon;
        patch.geocodedAt = nowIso();
      }
      return addressRepo.update(ctx, found.id, patch);
    }
  }

  const key = normalise(input.label);
  const match = existing.find((address) => normalise(address.label) === key);
  if (match) {
    const patch: Partial<Address> = { useCount: match.useCount + 1, lastUsedAt: nowIso() };
    if (input.lat !== undefined && match.lat === undefined) {
      patch.lat = input.lat;
      patch.lon = input.lon;
      patch.geocodedAt = nowIso();
    }
    return addressRepo.update(ctx, match.id, patch);
  }

  const address: Address = {
    id: crypto.randomUUID(),
    label: input.label,
    street: input.street,
    houseNumber: input.houseNumber,
    postalCode: input.postalCode,
    city: input.city,
    country: input.country ?? 'DE',
    lat: input.lat,
    lon: input.lon,
    geocodedAt: input.geocoded ? nowIso() : undefined,
    createdAt: nowIso(),
    useCount: 1,
    lastUsedAt: nowIso(),
  };
  return addressRepo.create(ctx, address);
}

// ---------------------------------------------------------------------------
// Shifts
// ---------------------------------------------------------------------------

/**
 * The shift a trip on this date belongs to, created if the driver has not
 * opened one. The odometer reading is a mandatory per-day field in the BMF
 * catalogue, so a shift auto-created here starts at zero and is flagged for
 * the driver to complete on the shift screen.
 */
export async function ensureShift(
  ctx: AuditContext,
  params: { date: IsoDate; driverId: Uuid; vehicleId: Uuid; odometerStartMeters?: number },
): Promise<Shift> {
  const existing = await shiftForDate(ctx.dataKey, params.date, params.driverId);
  if (existing) return existing;

  const shift: Shift = {
    id: crypto.randomUUID(),
    driverId: params.driverId,
    vehicleId: params.vehicleId,
    date: params.date,
    startedAt: `${params.date}T00:00:00.000Z`,
    odometerStartMeters: params.odometerStartMeters ?? 0,
    createdAt: nowIso(),
  };
  return shiftRepo.create(ctx, shift);
}

/**
 * Minutes covered by the `Pause von – bis` rows of the Fahrtennachweis.
 *
 * Wall-clock arithmetic, because that is how the times are written down. A
 * break whose end reads earlier than its start ran past midnight and is
 * counted into the next day rather than as a negative.
 */
export function breakMinutes(shift: Pick<Shift, 'breaks'>): number {
  return (shift.breaks ?? []).reduce((sum, pause) => {
    const from = minutesOfDay(pause.from);
    const to = minutesOfDay(pause.to);
    if (from === null || to === null) return sum;
    return sum + (to >= from ? to - from : to + 24 * 60 - from);
  }, 0);
}

function minutesOfDay(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

/** Paid working time: shift length less the recorded breaks. */
export function workingMinutes(shift: Shift): number | null {
  if (!shift.endedAt) return null;
  return Math.max(0, minutesBetween(shift.startedAt, shift.endedAt) - breakMinutes(shift));
}

export interface ShiftReconciliation {
  shift: Shift;
  tripCount: number;
  /** Sum of the recorded trip distances, in metres. */
  tripDistanceMeters: number;
  /** Odometer end minus start, or null while the shift is still open. */
  odometerDeltaMeters: number | null;
  /**
   * Odometer delta minus trip distance. Positive means kilometres were driven
   * that no trip accounts for — deadheading, the drive home, or a missing
   * trip. Negative is a red flag: trips claim more distance than the vehicle
   * moved.
   */
  differenceMeters: number | null;
  withinTolerance: boolean | null;
  grossRevenueCents: number;
  tipCents: number;
  revenueByPaymentMethod: Record<string, number>;
  /** Recorded break time, and shift length net of it. */
  breakMinutes: number;
  workingMinutes: number | null;
  /** The trips of the shift, in the order the Fahrtennachweis lists them. */
  trips: Trip[];
}

/**
 * Reconcile the shift's trips against the odometer.
 *
 * This is the first thing an auditor computes, so the app computes it too and
 * puts the result in the export. It matters more than usual here because the
 * configured system of record for distance is the routing API rather than the
 * odometer, and this is the cross-check that keeps that defensible.
 */
export async function reconcileShift(
  dataKey: CryptoKey,
  shift: Shift,
  toleranceMeters: number,
): Promise<ShiftReconciliation> {
  const trips = await tripsForShift(dataKey, shift.id);

  const tripDistanceMeters = trips.reduce((sum, trip) => sum + trip.distanceMeters, 0);
  const grossRevenueCents = trips.reduce((sum, trip) => sum + trip.fareGrossCents, 0);
  const tipCents = trips.reduce((sum, trip) => sum + trip.tipCents, 0);

  const revenueByPaymentMethod: Record<string, number> = {};
  for (const trip of trips) {
    revenueByPaymentMethod[trip.paymentMethod] =
      (revenueByPaymentMethod[trip.paymentMethod] ?? 0) + trip.fareGrossCents;
  }

  const odometerDeltaMeters =
    shift.odometerEndMeters !== undefined && shift.odometerStartMeters > 0
      ? shift.odometerEndMeters - shift.odometerStartMeters
      : null;

  const differenceMeters =
    odometerDeltaMeters === null ? null : odometerDeltaMeters - tripDistanceMeters;

  return {
    shift,
    tripCount: trips.length,
    tripDistanceMeters,
    odometerDeltaMeters,
    differenceMeters,
    withinTolerance:
      differenceMeters === null ? null : Math.abs(differenceMeters) <= toleranceMeters,
    grossRevenueCents,
    tipCents,
    revenueByPaymentMethod,
    breakMinutes: breakMinutes(shift),
    workingMinutes: workingMinutes(shift),
    trips,
  };
}

// ---------------------------------------------------------------------------
// Record lock
// ---------------------------------------------------------------------------

export function lockDueDate(tripDate: IsoDate, lockDays: number): IsoDate {
  return addDays(tripDate, lockDays);
}

export function isLocked(trip: Trip): boolean {
  return Boolean(trip.lockedAt);
}

export function shouldBeLocked(trip: Trip, lockDays: number, today = todayIso()): boolean {
  return !trip.lockedAt && today > lockDueDate(trip.date, lockDays);
}

/**
 * Freeze every trip past its window.
 *
 * Run at unlock rather than on a timer: the lock has to be a property of the
 * data, not of whether the app happened to be open on the seventh day.
 */
export async function sweepLocks(ctx: AuditContext, lockDays: number): Promise<number> {
  const today = todayIso();
  const cutoff = addDays(today, -lockDays);

  const { db } = await import('@/db/schema');
  const candidates = await db.trips
    .where('date')
    .below(cutoff)
    .filter((envelope) => envelope.lockedAt === null)
    .toArray();

  let locked = 0;
  for (const envelope of candidates) {
    const trip = await tripRepo.get(ctx.dataKey, envelope.id);
    if (!trip || trip.lockedAt) continue;
    await tripRepo.update(
      ctx,
      trip.id,
      { lockedAt: nowIso() },
      {
        action: 'lock',
        reason: `Automatische Festschreibung nach ${lockDays} Tagen (BMF 03.03.2022 Rn. 29)`,
      },
    );
    locked += 1;
  }
  return locked;
}

// ---------------------------------------------------------------------------
// Trip mutations
// ---------------------------------------------------------------------------

export interface TripInput {
  shiftId: Uuid;
  driverId: Uuid;
  vehicleId: Uuid;
  orderId?: Uuid;
  date: IsoDate;
  startedAt: string;
  endedAt: string;
  passengerId: Uuid;
  fromAddressId: Uuid;
  toAddressId: Uuid;
  tripType: TripType;
  distanceMeters: number;
  computedDistanceMeters?: number;
  distanceOverridden: boolean;
  distanceSource: Trip['distanceSource'];
  fareGrossCents: number;
  vatRatePercent: number;
  paymentMethod: PaymentMethod;
  tipCents: number;
  comment?: string;
}

export async function createTrip(ctx: AuditContext, input: TripInput): Promise<Trip> {
  const trip: Trip = {
    id: crypto.randomUUID(),
    ...input,
    tipCents: input.tipCents ?? 0,
    createdAt: nowIso(),
  };
  await tripRepo.create(ctx, trip);

  if (input.orderId) {
    await orderRepo.update(ctx, input.orderId, { tripId: trip.id });
  }

  return trip;
}

export class TripLockedError extends Error {
  constructor() {
    super('Diese Fahrt ist festgeschrieben. Korrekturen sind nur mit Begründung möglich.');
    this.name = 'TripLockedError';
  }
}

/**
 * Amend a trip.
 *
 * A locked trip requires a reason. The reason goes into the audit chain and is
 * printed alongside the change in every export, which is what BFH VI B 37/23
 * means by the history being apparent on ordinary inspection.
 */
export async function updateTrip(
  ctx: AuditContext,
  tripId: Uuid,
  patch: Partial<TripInput>,
  reason?: string,
): Promise<Trip> {
  const existing = await tripRepo.get(ctx.dataKey, tripId);
  if (!existing) throw new Error('Fahrt nicht gefunden.');

  if (existing.lockedAt && !reason?.trim()) throw new TripLockedError();

  return tripRepo.update(ctx, tripId, patch as Partial<Trip>, {
    reason: existing.lockedAt ? `Korrektur nach Festschreibung: ${reason}` : reason,
  });
}

/**
 * Link a booking-intake record to the trip it produced.
 * § 49 Abs. 4 Satz 4 PBefG keeps the two as separate records; this only
 * associates them.
 */
export async function linkOrderToTrip(
  ctx: AuditContext,
  order: Order,
  tripId: Uuid,
): Promise<void> {
  await orderRepo.update(ctx, order.id, { tripId });
  await tripRepo.update(ctx, tripId, { orderId: order.id });
}
